import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { revealHorizontally } from '../lib/revealHorizontally';
import { TABS } from './registry';

/**
 * A tabbed view switch, on the WAI-ARIA APG tabs pattern.
 *
 * Why a real tab widget rather than two buttons that swap a `div`: the panel has to be
 * announced as belonging to the tab that opened it, and the strip has to be ONE stop in the
 * tab order with arrows moving between the options. Buttons give a screen-reader user N stops
 * and no relationship, which on this screen means arrowing past "Discussion" without being
 * told the thread it controls has appeared.
 *
 * Roving tabindex, not `aria-activedescendant`: focus really moves, so the browser scrolls the
 * strip and paints the focus ring itself. The APG allows either and this one has fewer places
 * to be wrong.
 *
 * SELECTION FOLLOWS FOCUS, which is the APG's default and correct here only because every
 * panel is already in memory — the admin page, the one caller today, keeps all its sections
 * mounted and merely hides the inactive ones. If a panel ever costs a request, this has to become manual activation
 * (`Enter`/`Space`), because arrowing across a strip would otherwise fire one fetch per tab.
 */

export interface TabItem<Id extends string> {
  id: Id;
  label: string;
  /** Shown as a pill beside the name. Omitted rather than `0` when there is nothing to count. */
  count?: number | undefined;
}

export interface TabsProps<Id extends string> {
  /** Names the strip for a screen reader. Never rendered. */
  label: string;
  items: ReadonlyArray<TabItem<Id>>;
  active: Id;
  onSelect: (id: Id) => void;
  /**
   * The id prefix that ties each tab to its panel. Supplied rather than generated so the
   * caller can put `tabPanelProps` on a panel it renders itself — the panel is the caller's
   * content, and wrapping it here would force every panel into one container's padding.
   */
  idPrefix?: string;
}

const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
const panelId = (prefix: string, id: string) => `${prefix}-panel-${id}`;

/**
 * The attributes a panel needs to be announced as this tab's panel. Returned rather than
 * rendered so the panel stays the caller's element.
 *
 * `tabIndex={0}` is deliberate and is a real WCAG requirement rather than a flourish: a panel
 * whose content scrolls must be reachable by keyboard, and the panel itself is the only thing
 * in it that is not already focusable.
 */
export function tabPanelProps(prefix: string, id: string) {
  return {
    role: 'tabpanel' as const,
    id: panelId(prefix, id),
    'aria-labelledby': tabId(prefix, id),
    tabIndex: 0,
  };
}

export function Tabs<Id extends string>({
  label,
  items,
  active,
  onSelect,
  idPrefix,
}: TabsProps<Id>) {
  const generated = useId();
  const prefix = idPrefix ?? generated;
  const strip = useRef<HTMLDivElement>(null);

  useEffect(() => {
    revealHorizontally(
      strip.current,
      strip.current?.querySelector(`#${CSS.escape(tabId(prefix, active))}`),
    );
  }, [active, prefix]);

  const move = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const index = items.findIndex((item) => item.id === active);
      if (index === -1) return;

      // Home and End are part of the pattern, not extras: on a strip of four they are the
      // only way to reach either end without counting.
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % items.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = items.length - 1;
      else return;

      event.preventDefault();
      const target = items[next];
      if (target === undefined) return;
      onSelect(target.id);
      // Focus follows the selection, and the element it moves to does not exist until this
      // render commits — so it is read out of the strip rather than held as a ref per tab.
      requestAnimationFrame(() => {
        strip.current
          ?.querySelector<HTMLButtonElement>(`#${CSS.escape(tabId(prefix, target.id))}`)
          ?.focus();
      });
    },
    [items, active, onSelect, prefix],
  );

  return (
    // A tablist has no HTML element of its own — the role IS the semantic answer here, which
    // is why there is no suppression above this line to explain one away.
    <div className={TABS.list} role="tablist" aria-label={label} ref={strip} onKeyDown={move}>
      {items.map((item) => {
        const selected = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={tabId(prefix, item.id)}
            aria-selected={selected}
            aria-controls={panelId(prefix, item.id)}
            // The whole strip is one tab stop. Arrows move within it.
            tabIndex={selected ? 0 : -1}
            className={TABS.tab}
            onClick={() => onSelect(item.id)}
          >
            {item.label}
            {item.count === undefined ? null : (
              <span className={TABS.count}>
                {item.count}
                {/* The number alone is ambiguous read aloud — "Discussion 4" could be a name. */}
                <span className="vh"> items</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  prefix: string;
  id: string;
  children: ReactNode;
}

/** The panel, when the caller has no element of its own to put the attributes on. */
export function TabPanel({ prefix, id, children }: TabPanelProps) {
  return <div {...tabPanelProps(prefix, id)}>{children}</div>;
}
